import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { generatePath, useHistory } from 'react-router-dom';

import { TransactionCurrencyType } from 'common/api/autogenerated/client-area';

import { DepositMethodsProps } from 'common/components/DepositButton/DepositButton.component';
import { APPLE_PAY_METHOD_ID, APPLE_PAY_WINDOW_NAME } from 'common/components/DepositButton/DepositButtons/constants';
import { FrameEvents } from 'common/components/Frame/types';

import { useBowser, useCustomer, useI18n, usePopup, useTracking } from 'common/hooks';

import { getDepositUrl } from 'views/wallet-new/redux/wallet.api';

import { routes } from 'routes/routes.constants';

import depositCryptoImage from '../../../assets/images/payment-method-deposit-crypto.png';
import ConfirmVerificationPopupComponent from '../ConfirmVerificationPopup/ConfirmVerificationPopup.component';
import { DepositMethod, DepositMethodSkeleton } from './DepositMethod.component';
import { DEFAULT_DEPOSIT_CURRENCY, DEFAULT_RECEIVE_CURRENCY } from './constants';
import { convertPaymentMethodsFromServer, requestPaymentMethods } from './helpers';
import './index.css';
import { AlternativeFiatPaymentMetod, FiatPaymentMetod, PaymentMethod } from './types';

export const DepositMethods: React.FC<React.PropsWithChildren<DepositMethodsProps>> = React.memo(
  ({ params, currency, type, config }) => {
    const { push } = useHistory();
    const { translateToText } = useI18n();
    const { isMobile, isIOS } = useBowser();
    const { show, hide } = usePopup();
    const { track } = useTracking();
    const { isFiatDepositEnabled, isCryptoDepositEnabled } = useCustomer();

    const fiatCurrency = useMemo(() => currency || DEFAULT_DEPOSIT_CURRENCY, [currency]);
    const cryptoCurrency = useMemo(() => currency || DEFAULT_RECEIVE_CURRENCY, [currency]);

    const [loading, toggleLoading] = useState(true);
    const [standartFiatPaymentMethods, updateStandartFiatPaymentMethods] = useState<FiatPaymentMetod[]>([]);
    const [alternativePaymentMethods, updateAlternativePaymentMethods] = useState<AlternativeFiatPaymentMetod[]>([]);

    const isCryptoDeposit = useMemo(() => type === TransactionCurrencyType.CRYPTO, [type]);
    const isFiatDeposit = useMemo(() => type === TransactionCurrencyType.FIAT, [type]);

    const displayCryptoDeposit = useMemo(
      () => (!type || isCryptoDeposit) && config?.crypto?.isDepositEnabled,
      [config?.crypto?.isDepositEnabled, isCryptoDeposit, type],
    );
    const displayFiatDeposit = useMemo(
      () => (!type || isFiatDeposit) && config?.fiat?.isDepositEnabled,
      [config?.fiat?.isDepositEnabled, isFiatDeposit, type],
    );

    const isOnlyOneType = useMemo(
      () => [displayCryptoDeposit, displayFiatDeposit].some((i) => !i),
      [displayCryptoDeposit, displayFiatDeposit],
    );

    useEffect(
      function init() {
        if (isCryptoDeposit) {
          toggleLoading(false);
          return;
        }
        requestPaymentMethods(fiatCurrency)
          .then(async (response) => {
            const cashierUrl = await getDepositUrl();
            return convertPaymentMethodsFromServer(response, fiatCurrency, cashierUrl, params);
          })
          .then(({ alternative, fiat }) => {
            updateAlternativePaymentMethods(alternative);
            updateStandartFiatPaymentMethods(fiat);
            toggleLoading(false);
          });
      },
      [currency, fiatCurrency, isCryptoDeposit, params, type],
    );

    const listenTabEvents = useCallback(
      (_event: MessageEvent) => {
        if (Object.values(FrameEvents).includes(_event.data.type)) {
          const applePayWindow = window.open('', APPLE_PAY_WINDOW_NAME);
          applePayWindow?.close();
          hide();
        }
      },
      [hide],
    );

    useEffect(() => {
      window.addEventListener('message', listenTabEvents);

      return () => {
        window.removeEventListener('message', listenTabEvents);
      };
    }, [listenTabEvents]);

    const onClickPaymentMethod = useCallback(
      (event: React.MouseEvent<HTMLLIElement, MouseEvent>, method: PaymentMethod) => {
        if (
          (method.type === 'fiat' && !isFiatDepositEnabled) ||
          (method.type === 'crypto' && !isCryptoDepositEnabled)
        ) {
          event.stopPropagation();
          event.preventDefault();
          show(ConfirmVerificationPopupComponent({ onCloseHandler: hide }));
          return;
        }

        if (method.id) {
          track('deposit:method:choose', { id: method.id });
        }

        if (method.external) {
          window.open(method.url, APPLE_PAY_WINDOW_NAME);
          return;
        }

        hide();

        push(method.url);
      },
      [hide, isCryptoDepositEnabled, isFiatDepositEnabled, push, show, track],
    );

    const fiatPaymentMethods = useMemo(
      () => [...standartFiatPaymentMethods, ...alternativePaymentMethods],
      [alternativePaymentMethods, standartFiatPaymentMethods],
    );

    const renderCryptoPaymentMethods = useCallback(() => {
      if (displayCryptoDeposit) {
        return (
          <>
            {isMobile && !isOnlyOneType && (
              <div className='deposit-list__section-title'>{translateToText('mobile:deposit:label:crypro')}</div>
            )}
            <DepositMethod
              id='crypto-deposit'
              type='crypto'
              label={translateToText('mobile:deposit:crypto')}
              url={generatePath(routes.getReceivePage(), {
                currency: cryptoCurrency,
              })}
              image={depositCryptoImage}
              onClick={onClickPaymentMethod}
            />
          </>
        );
      }
      return null;
    }, [cryptoCurrency, displayCryptoDeposit, isMobile, isOnlyOneType, onClickPaymentMethod, translateToText]);

    const renderFiatPaymentMethods = useCallback(() => {
      if (!displayFiatDeposit) return null;

      if (loading) {
        return (
          <>
            {isMobile && !isOnlyOneType && (
              <span className='deposit-list__section-title deposit-list__item-skeleton'>
                {translateToText('mobile:deposit:label:fiat')}
              </span>
            )}
            <DepositMethodSkeleton />
            <DepositMethodSkeleton />
            <DepositMethodSkeleton />
            <DepositMethodSkeleton />
            <DepositMethodSkeleton />
            <DepositMethodSkeleton />
            <DepositMethodSkeleton />
          </>
        );
      }

      return (
        <>
          {isMobile && !isOnlyOneType && (
            <div className='deposit-list__section-title'>{translateToText('mobile:deposit:label:fiat')}</div>
          )}
          {fiatPaymentMethods.map((method) => (
            <DepositMethod
              key={method.id}
              onClick={onClickPaymentMethod}
              hidden={method.name === APPLE_PAY_METHOD_ID && !isIOS}
              {...method}
            />
          ))}
        </>
      );
    }, [
      displayFiatDeposit,
      fiatPaymentMethods,
      isIOS,
      isMobile,
      isOnlyOneType,
      loading,
      onClickPaymentMethod,
      translateToText,
    ]);

    return (
      <ul className='deposit-list' data-test='deposit-methods'>
        {renderCryptoPaymentMethods()}
        {renderFiatPaymentMethods()}
      </ul>
    );
  },
);

export default (props: DepositMethodsProps) => ({
  component: <DepositMethods {...props} />,
  useBottomSheet: true,
  ...props,
});
